import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Timer,
  UserRound,
  XCircle,
} from "lucide-react";
import API from "../../../services/api";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import EmployeeSelect from "../../common/EmployeeSelect";
import {
  acceptDetectedOt,
  enrollOtOccurrenceSlots,
  fetchAttendance,
  fetchOtAllocationRegister,
  saveOtAllocation,
  setOtAllocationStatus,
} from "../../../Moduels/attendance/attendanceApi";
import { fetchWfHolidays } from "../../../Moduels/attendance/wfApi";
import {
  buildAttendanceDayMap,
  buildCalendarCells,
  buildHolidayDayMap,
  mergeAttendanceDisplayMap,
  monthRange,
  parseWorkDate,
  weekOffWeekdaysFromAttendance,
} from "../../../Moduels/attendance/attendanceCalendarUtils";
import { ATTENDANCE_STATUS_CONFIG } from "../../../Moduels/attendance/indianCalendarUtils";
import { useOtHoursAllocationNav } from "../../../hooks/useOtHoursAllocationNav";
import OtAllocationCalendar from "./OtAllocationCalendar";
import OtPayMath from "./OtPayMath";
import "../../../styles/otHoursAllocation.css";

function currentMonthKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function shiftMonth(monthKey, delta) {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function formatMonthLimit(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "No limit";
  return `${formatHours(n)}h`;
}

function isoFromLocalDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function rateDateForMonth(monthKey) {
  const [y, m] = String(monthKey || "").split("-").map(Number);
  if (!y || !m) return isoFromLocalDate(new Date());
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today < start) return isoFromLocalDate(start);
  if (today > end) return isoFromLocalDate(end);
  return isoFromLocalDate(today);
}

function formatPunchTime(value) {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/T(\d{2}:\d{2})/) || s.match(/^(\d{2}:\d{2})/);
  return m ? m[1] : s;
}

function formatDateLabel(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_CLASS = {
  PENDING: "ot-badge ot-badge--pending",
  APPROVED: "ot-badge ot-badge--approved",
  REJECTED: "ot-badge ot-badge--rejected",
  CANCELLED: "ot-badge ot-badge--cancelled",
};

export default function OtHoursAllocationPage() {
  const { user: me } = useContext(AuthContext);
  const role = (me?.role || me?.user?.role || "").toLowerCase();
  const roleNorm = role === "super_admin" ? "superadmin" : role;
  const canEdit = ["admin", "hr", "manager", "superadmin", "super_admin"].includes(role);

  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const { loaded: featureLoaded, enabled: featureEnabled } = useOtHoursAllocationNav();

  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calLoading, setCalLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [detail, setDetail] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payRevision, setPayRevision] = useState(0);

  const [dayStatusMap, setDayStatusMap] = useState({});
  const [holidayMap, setHolidayMap] = useState({});
  const [weekOffWeekdays, setWeekOffWeekdays] = useState(null);
  const [attendanceRowsByDay, setAttendanceRowsByDay] = useState({});

  const [addForm, setAddForm] = useState({
    employee_id: "",
    work_date: new Date().toISOString().slice(0, 10),
    hours: "",
    remarks: "",
  });
  const [addError, setAddError] = useState("");
  const [occurrenceModeHint, setOccurrenceModeHint] = useState(false);

  const [y, m] = monthKey.split("-").map(Number);
  const monthLabel = useMemo(() => {
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }, [y, m]);

  const range = useMemo(() => monthRange(monthKey), [monthKey]);
  const { cells, weekdayHeaders } = useMemo(() => buildCalendarCells(monthKey), [monthKey]);

  const loadRegister = useCallback(async () => {
    if (!featureEnabled) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchOtAllocationRegister({
        year: y,
        month: m,
        employee_id: employeeId || undefined,
        status_filter: statusFilter || undefined,
        source_filter: sourceFilter || undefined,
        page: 1,
        page_size: 200,
      });
      setRegister(res);
    } catch (e) {
      setError(e.message || "Could not load OT register");
      setRegister(null);
    } finally {
      setLoading(false);
    }
  }, [featureEnabled, y, m, employeeId, statusFilter, sourceFilter]);

  const loadCalendarContext = useCallback(async () => {
    if (!featureEnabled || !employeeId || !range) {
      setDayStatusMap({});
      setHolidayMap({});
      setAttendanceRowsByDay({});
      return;
    }
    setCalLoading(true);
    try {
      const [attRows, holidayRows, settingsRes] = await Promise.all([
        fetchAttendance({
          employee_id: employeeId,
          from_date: range.from_date,
          to_date: range.to_date,
        }),
        fetchWfHolidays({ from_date: range.from_date, to_date: range.to_date }).catch(() => []),
        API.get("/organisation/me/hr-settings").catch(() => null),
      ]);

      const att =
        settingsRes?.data?.attendance_settings ||
        settingsRes?.data?.hr_settings?.attendance ||
        {};
      const wo = weekOffWeekdaysFromAttendance(att);
      setWeekOffWeekdays(wo);

      const hMap = buildHolidayDayMap(holidayRows, monthKey);
      setHolidayMap(hMap);

      const rowMap = {};
      for (const r of attRows || []) {
        const parts = parseWorkDate(r.work_date);
        if (!parts || parts.monthKey !== monthKey) continue;
        rowMap[parts.day] = r;
      }
      setAttendanceRowsByDay(rowMap);

      const base = buildAttendanceDayMap(attRows, monthKey);
      const merged = mergeAttendanceDisplayMap(base, cells, hMap, {
        weekOffWeekdays: wo,
        rowsByDay: rowMap,
      });
      setDayStatusMap(merged);
    } catch (e) {
      setDayStatusMap({});
      setHolidayMap({});
      setAttendanceRowsByDay({});
      setError((prev) => prev || e.message || "Could not load attendance calendar");
    } finally {
      setCalLoading(false);
    }
  }, [featureEnabled, employeeId, range, monthKey, cells]);

  useEffect(() => {
    if (featureEnabled) loadRegister();
  }, [featureEnabled, loadRegister]);

  useEffect(() => {
    if (featureEnabled) loadCalendarContext();
  }, [featureEnabled, loadCalendarContext]);

  useEffect(() => {
    setSelectedDay(null);
    setDetail(null);
  }, [monthKey, employeeId]);

  const policySummary = register?.summary || {};
  const employeeSelected = Boolean(employeeId);
  const summary = employeeSelected
    ? policySummary
    : {
        total_ot_hours: 0,
        total_ot_count: 0,
        ot_days: 0,
        ot_events: 0,
        pending_approval: 0,
        approved_ot: 0,
        manual_ot: 0,
        automatic_ot: 0,
        max_ot_hours_per_day: policySummary.max_ot_hours_per_day,
        max_ot_hours_per_month: policySummary.max_ot_hours_per_month,
      };
  const hoursSource = register?.hours_source || "automatic";
  const allocation = register?.allocation || { method: "hours", unit: "hours", label: "Count by hours" };
  const countMode = allocation.unit === "ot";
  const payMethod = register?.pay_method || {};
  const occurrenceMode = Boolean(payMethod.occurrence_mode);
  const occurrenceTracker = register?.occurrence_tracker || [];
  const expandRoles = (payMethod.occurrence_expand_roles || ["hr", "manager", "admin", "superadmin"]).map((r) =>
    String(r).toLowerCase()
  );
  const canExpandSlots =
    canEdit &&
    (expandRoles.includes(roleNorm) ||
      (roleNorm === "admin" && (expandRoles.includes("admin") || expandRoles.includes("superadmin"))));

  const otByDay = useMemo(() => {
    const map = {};
    if (!employeeId) return map;
    for (const row of register?.rows || []) {
      const parts = parseWorkDate(row.work_date);
      if (!parts || parts.monthKey !== monthKey) continue;
      const existing = map[parts.day];
      if (!existing) {
        map[parts.day] = row;
        continue;
      }
      // Prefer pending, then approved, then highest hours
      const rank = (r) =>
        (r.status === "PENDING" ? 3 : r.status === "APPROVED" ? 2 : 1) * 1000 + (Number(r.ot_hours) || 0);
      if (rank(row) > rank(existing)) map[parts.day] = row;
    }
    return map;
  }, [employeeId, register?.rows, monthKey]);

  const selectedIso =
    selectedDay != null ? `${monthKey}-${String(selectedDay).padStart(2, "0")}` : null;
  const cardPayDate = selectedIso || rateDateForMonth(monthKey);
  const selectedOt = selectedDay != null ? otByDay[selectedDay] || null : null;
  const selectedStatus = selectedDay != null ? dayStatusMap[selectedDay] : null;
  const selectedHoliday = selectedDay != null ? holidayMap[selectedDay] : null;
  const selectedAttRow = selectedDay != null ? attendanceRowsByDay[selectedDay] : null;

  const openAdd = (opts = {}) => {
    if (!canEdit) return;
    setOccurrenceModeHint(Boolean(opts.occurrence) || occurrenceMode);
    setAddForm({
      employee_id: opts.employee_id || employeeId || "",
      work_date: opts.work_date || new Date().toISOString().slice(0, 10),
      hours: occurrenceMode || opts.occurrence ? "1" : "",
      remarks: occurrenceMode || opts.occurrence ? "OT occurrence" : "",
    });
    setAddError("");
    setAddOpen(true);
  };

  const openAddOccurrence = (empId) => openAdd({ employee_id: empId, occurrence: true });

  const onEnrollMore = async (emp) => {
    if (!canExpandSlots || !emp) return;
    setSaving(true);
    setError("");
    try {
      await enrollOtOccurrenceSlots({
        employee_id: emp.employee_id,
        year: y,
        month: m,
        slot_count: (emp.slot_count || 10) + 1,
      });
      await loadRegister();
    } catch (e) {
      setError(e.message || "Could not increase occurrence slots");
    } finally {
      setSaving(false);
    }
  };

  const onApprove = async (row) => {
    setSaving(true);
    try {
      if (row.status === "PENDING" && row.source === "automatic") {
        await acceptDetectedOt(row.attendance_id);
      } else {
        await setOtAllocationStatus({ attendance_id: row.attendance_id, status: "APPROVED" });
      }
      await loadRegister();
      setDetail(null);
    } catch (e) {
      setError(e.message || "Approve failed");
    } finally {
      setSaving(false);
    }
  };

  const onReject = async (row) => {
    setSaving(true);
    try {
      await setOtAllocationStatus({ attendance_id: row.attendance_id, status: "REJECTED" });
      await loadRegister();
      setDetail(null);
    } catch (e) {
      setError(e.message || "Reject failed");
    } finally {
      setSaving(false);
    }
  };

  const submitAdd = async () => {
    if (!canEdit) return;
    const hours = Number(addForm.hours);
    if (!addForm.employee_id) {
      setAddError("Employee is required.");
      return;
    }
    if (!addForm.work_date) {
      setAddError("Date is required.");
      return;
    }
    if (!Number.isFinite(hours) || hours <= 0) {
      setAddError("OT Hours must be greater than zero.");
      return;
    }
    setSaving(true);
    setAddError("");
    try {
      await saveOtAllocation({
        employee_id: addForm.employee_id,
        work_date: addForm.work_date,
        hours,
        remarks: addForm.remarks || undefined,
      });
      setAddOpen(false);
      setAddForm({
        employee_id: employeeId || "",
        work_date: new Date().toISOString().slice(0, 10),
        hours: "",
        remarks: "",
      });
      await loadRegister();
      await loadCalendarContext();
    } catch (e) {
      setAddError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setDetail(null);
  };

  if (featureLoaded && !featureEnabled) {
    return <Navigate to="/attendance/apply-calendar" replace />;
  }

  if (!featureLoaded) {
    return (
      <div className="ot-page">
        <div className="ot-hero">
          <h1>Overtime</h1>
          <p>Loading overtime workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ot-page">
      <div className="ot-hero">
        <div className="ot-hero__glow" aria-hidden />
        <div className="ot-hero__content">
          <p className="ot-hero__eyebrow">
            <Clock size={14} aria-hidden /> Attendance · OT Hours Source of Truth
          </p>
          <h1>Overtime</h1>
          <p>
            Month calendar for the selected employee — attendance, holidays, week-offs, and OT on each
            day. Approve records here; payroll only pays <strong>Approved</strong> OT.
          </p>
          {occurrenceMode ? (
            <p className="ot-hero__mode">
              Mode: Fixed Amount Per OT Occurrence — tick checkboxes track each OT day; payroll pays approved ticks × ₹
              {Number(payMethod.fixed_amount || 0).toLocaleString("en-IN")}.
            </p>
          ) : hoursSource === "manual" ? (
            <p className="ot-hero__mode">Mode: Manual Entry — no automatic OT from punches.</p>
          ) : (
            <p className="ot-hero__mode">Mode: Automatic from Attendance — OT from shift end vs actual OUT.</p>
          )}
          <p className="ot-hero__mode">
            Allocation: {allocation.label || "Count by hours"}
            {countMode
              ? " — each qualifying entry is counted as OT. Hours stay on the record."
              : " — 1 hour worked = 1 OT hour."}
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="ot-add-btn"
            onClick={() =>
              openAdd({
                employee_id: employeeId || "",
                work_date: selectedIso || new Date().toISOString().slice(0, 10),
              })
            }
          >
            <Plus size={16} /> {occurrenceMode ? "Add OT Occurrence" : "Add OT"}
          </button>
        ) : null}
      </div>

      <div className="ot-kpi-row">
        {[
          countMode
            ? { label: "Total OT Count", value: formatHours(summary.total_ot_count) || "0", tone: "accent" }
            : { label: "Total OT Hours", value: `${formatHours(summary.total_ot_hours)}h`, tone: "accent" },
          countMode
            ? { label: "Hours on OT counts", value: `${formatHours(summary.total_ot_hours)}h` }
            : null,
          { label: "OT Days", value: summary.ot_days || 0 },
          { label: "OT Events", value: summary.ot_events || 0 },
          { label: "Pending Approval", value: summary.pending_approval || 0, tone: "warn" },
          { label: "Approved OT", value: summary.approved_ot || 0, tone: "ok" },
          { label: "Manual OT", value: summary.manual_ot || 0 },
          { label: "Automatic OT", value: summary.automatic_ot || 0 },
          hoursSource === "manual"
            ? null
            : {
                label: "Maximum Hours Limit",
                value: formatMonthLimit(summary.max_ot_hours_per_month),
                tone: "limit",
              },
        ].filter(Boolean).map((k) => (
          <div key={k.label} className={`ot-kpi${k.tone ? ` ot-kpi--${k.tone}` : ""}`}>
            <div className="ot-kpi__label">{k.label}</div>
            <div className="ot-kpi__value">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="ot-panel ot-filters mb-4">
        <div className="ot-toolbar">
          <div className="flex items-center gap-2">
            <button type="button" className="ot-nav-btn" onClick={() => setMonthKey(shiftMonth(monthKey, -1))} aria-label="Previous month">
              <ChevronLeft size={18} />
            </button>
            <span className="font-bold text-slate-800 min-w-[140px] text-center">{monthLabel}</span>
            <button type="button" className="ot-nav-btn" onClick={() => setMonthKey(shiftMonth(monthKey, 1))} aria-label="Next month">
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="ot-filter-selects">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="">All sources</option>
              <option value="automatic">Automatic</option>
              <option value="manual">Manual</option>
            </select>
          </div>
        </div>
        <div className="mt-3 max-w-3xl">
          <EmployeeSelect value={employeeId} onChange={(id) => setEmployeeId(id || "")} label="Employee *" />
          <OtPayMath
            employeeId={employeeId}
            workDate={cardPayDate}
            hours={selectedOt?.ot_hours}
            canEdit={canEdit}
            autoScroll
            revision={payRevision}
            onSaved={() => setPayRevision((n) => n + 1)}
          />
        </div>
      </div>

      {error ? (
        <div className="ot-error-banner mb-3" role="alert">
          <AlertTriangle size={16} aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      {occurrenceMode ? (
        <div className="ot-panel ot-occurrence mb-4">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-800 m-0">OT Occurrence Tracker</h2>
              <p className="text-xs text-slate-500 mt-1 mb-0">
                Each checkbox = one OT day/occurrence. Tick 1 = first OT, tick 2 = second, and so on. Payroll pays{" "}
                <strong>Approved</strong> ticks × ₹{Number(payMethod.fixed_amount || 0).toLocaleString("en-IN")}.
              </p>
            </div>
            {canEdit ? (
              <button type="button" className="ot-add-btn ot-add-btn--sm" onClick={() => openAddOccurrence(employeeId)}>
                <Plus size={14} /> Tick new occurrence
              </button>
            ) : null}
          </div>
          {!employeeSelected ? (
            <div className="ot-empty ot-empty--sm">
              <UserRound size={22} aria-hidden />
              <p>Select an employee to see their OT occurrences. Nothing is shown until one employee is chosen.</p>
            </div>
          ) : loading && !occurrenceTracker.length ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading occurrence records…</p>
          ) : occurrenceTracker.length === 0 ? (
            <div className="ot-empty ot-empty--sm">
              <Timer size={22} aria-hidden />
              <p>No OT occurrences this month yet. Add the first tick to start the employee tracker.</p>
            </div>
          ) : (
            <div className="ot-occurrence-list">
              {occurrenceTracker.map((emp) => (
                <div key={emp.employee_id} className="ot-occurrence-card">
                  <div className="ot-occurrence-card__head">
                    <div>
                      <strong>{emp.employee_name}</strong>
                      {emp.employee_code ? <span className="ot-occurrence-card__code">{emp.employee_code}</span> : null}
                    </div>
                    <div className="ot-occurrence-card__meta">
                      <span>
                        {emp.used_count}/{emp.slot_count} used
                      </span>
                      <span className="ot-occurrence-card__pay">
                        Est. ₹{Number(emp.estimated_pay || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <div className="ot-occurrence-slots" role="group" aria-label={`${emp.employee_name} OT occurrences`}>
                    {emp.slots.map((slot) => {
                      const title = slot.ticked
                        ? `#${slot.index} · ${formatDateLabel(slot.work_date)} · ${slot.status}`
                        : `#${slot.index} · available — click to record OT`;
                      return (
                        <label
                          key={`${emp.employee_id}-${slot.index}`}
                          className={`ot-occ-slot${slot.ticked ? " ot-occ-slot--on" : ""}${
                            slot.status === "APPROVED" ? " ot-occ-slot--ok" : ""
                          }${slot.status === "PENDING" ? " ot-occ-slot--pending" : ""}${
                            slot.status === "REJECTED" || slot.status === "CANCELLED" ? " ot-occ-slot--bad" : ""
                          }`}
                          title={title}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(slot.ticked)}
                            disabled={Boolean(slot.ticked) || !canEdit || saving}
                            onChange={() => {
                              if (!slot.ticked) openAddOccurrence(emp.employee_id);
                            }}
                            aria-label={title}
                          />
                          <span>{slot.index}</span>
                        </label>
                      );
                    })}
                  </div>
                  {canExpandSlots ? (
                    <button
                      type="button"
                      className="ot-occ-enroll"
                      disabled={saving}
                      onClick={() => onEnrollMore(emp)}
                    >
                      <Plus size={12} /> Enroll +1 checkbox
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {!employeeId ? (
        <div className="ot-select-employee-hint">
          <UserRound size={16} aria-hidden />
          Select an employee to open their OT calendar for {monthLabel} — attendance, holidays, week-offs, and OT days.
        </div>
      ) : null}

      <div className="ot-cal-layout">
        <div className="ot-panel ot-register-wrap">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-bold text-slate-800 m-0">OT Calendar</h2>
              <p className="text-xs text-slate-500 mt-0.5 mb-0">
                {employeeId
                  ? `${monthLabel} · attendance + holidays + week-offs + OT`
                  : "Choose an employee to load the month grid"}
              </p>
            </div>
            <span className="text-xs text-slate-500">
              {Object.keys(otByDay).length} OT day{Object.keys(otByDay).length === 1 ? "" : "s"} this month
            </span>
          </div>

          {!employeeId ? (
            <div className={`ot-cal-grid ot-cal-grid--idle`}>
              {weekdayHeaders.map((name) => (
                <div key={name} className="ot-cal-head">
                  {name.slice(0, 3)}
                </div>
              ))}
              {cells.map((cell) =>
                cell.empty ? (
                  <div key={cell.key} className="ot-cal-cell ot-cal-cell--empty" aria-hidden />
                ) : (
                  <div key={cell.key} className="ot-cal-cell ot-cal-cell--idle-day">
                    <span className="ot-cal-daynum">{cell.day}</span>
                  </div>
                )
              )}
            </div>
          ) : loading || calLoading ? (
            <p className="text-sm text-slate-500 py-10 text-center ot-loading-pulse">Loading OT calendar…</p>
          ) : (
            <OtAllocationCalendar
              monthKey={monthKey}
              cells={cells}
              weekdayHeaders={weekdayHeaders}
              dayStatusMap={dayStatusMap}
              holidayMap={holidayMap}
              weekOffWeekdays={weekOffWeekdays}
              otByDay={otByDay}
              countMode={countMode}
              selectedDay={selectedDay}
              busy={saving}
              onDayClick={handleDayClick}
            />
          )}
        </div>

        {employeeId && selectedDay != null ? (
          <aside className="ot-panel ot-day-panel">
            <p className="ot-day-panel__eyebrow">Selected day</p>
            <h3 className="ot-day-panel__title">{formatDateLabel(selectedIso)}</h3>

            <div className="ot-day-panel__chips">
              {selectedStatus ? (
                <span className={`ot-cal-status ot-cal-status--${String(selectedStatus).toLowerCase()}`}>
                  {ATTENDANCE_STATUS_CONFIG[selectedStatus]?.label || selectedStatus}
                </span>
              ) : null}
              {selectedHoliday ? (
                <span className="ot-day-chip ot-day-chip--holiday">{selectedHoliday.name}</span>
              ) : null}
            </div>

            {selectedAttRow ? (
              <dl className="ot-detail-dl ot-detail-dl--compact">
                <div>
                  <dt>IN</dt>
                  <dd>{formatPunchTime(selectedAttRow.time_in || selectedAttRow.check_in)}</dd>
                </div>
                <div>
                  <dt>OUT</dt>
                  <dd>{formatPunchTime(selectedAttRow.time_out || selectedAttRow.check_out)}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-xs text-slate-500 mt-2">No attendance punches saved for this day.</p>
            )}

            {selectedOt ? (
              <div className="ot-day-ot-block">
                <div className="flex items-center justify-between gap-2">
                  <strong>
                    {countMode
                      ? `OT count ${formatHours(selectedOt.ot_count)}`
                      : `OT ${formatHours(selectedOt.ot_hours)}h`}
                  </strong>
                  <span className={STATUS_CLASS[selectedOt.status] || "ot-badge"}>{selectedOt.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-0">
                  {countMode ? `Hours on this count: ${formatHours(selectedOt.ot_hours)}h · ` : ""}
                  {selectedOt.source_label || (selectedOt.source === "manual" ? "Manual" : "Automatic")}
                </p>
                <div className="ot-day-actions">
                  <button type="button" className="ot-day-action ot-day-action--ghost" onClick={() => setDetail(selectedOt)}>
                    View details
                  </button>
                  {canEdit && selectedOt.status === "PENDING" ? (
                    <>
                      <button
                        type="button"
                        className="ot-day-action ot-day-action--ok"
                        disabled={saving}
                        onClick={() => onApprove(selectedOt)}
                      >
                        <CheckCircle2 size={14} aria-hidden />
                        Approve
                      </button>
                      <button
                        type="button"
                        className="ot-day-action ot-day-action--bad"
                        disabled={saving}
                        onClick={() => onReject(selectedOt)}
                      >
                        <XCircle size={14} aria-hidden />
                        Reject
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : canEdit ? (
              <button
                type="button"
                className="ot-add-btn ot-add-btn--sm mt-3"
                onClick={() => openAdd({ employee_id: employeeId, work_date: selectedIso })}
              >
                <Plus size={14} /> Add OT for this day
              </button>
            ) : (
              <p className="text-xs text-slate-500 mt-3">No OT recorded on this day.</p>
            )}
          </aside>
        ) : null}
      </div>

      {detail ? (
        <div className="ot-modal-backdrop" role="presentation" onClick={() => setDetail(null)}>
          <div className="ot-modal ot-drawer" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900">OT Details</h2>
            <p className="text-sm text-slate-500 mt-1">
              {detail.employee_name} · {formatDateLabel(detail.work_date)}
            </p>
            <dl className="ot-detail-dl">
              <div>
                <dt>{countMode ? "OT Count" : "OT Hours"}</dt>
                <dd>{countMode ? formatHours(detail.ot_count) : `${formatHours(detail.ot_hours)}h`}</dd>
              </div>
              {countMode ? (
                <div>
                  <dt>Hours on this count</dt>
                  <dd>{formatHours(detail.ot_hours)}h</dd>
                </div>
              ) : null}
              <div>
                <dt>OT Source</dt>
                <dd>{detail.source_label}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{detail.status}</dd>
              </div>
              {detail.calculation_basis ? (
                <div>
                  <dt>Calculation Basis</dt>
                  <dd>{detail.calculation_basis}</dd>
                </div>
              ) : null}
              {detail.scheduled_shift_end ? (
                <div>
                  <dt>Scheduled Shift End</dt>
                  <dd>{detail.scheduled_shift_end}</dd>
                </div>
              ) : null}
              {detail.actual_out ? (
                <div>
                  <dt>Actual OUT</dt>
                  <dd>{String(detail.actual_out).slice(11, 16) || detail.actual_out}</dd>
                </div>
              ) : null}
              {detail.remarks ? (
                <div>
                  <dt>Remarks</dt>
                  <dd>{detail.remarks}</dd>
                </div>
              ) : null}
            </dl>
            {detail.source === "automatic" ? (
              <p className="text-xs text-slate-500 mt-3">
                Calculated OT uses Scheduled Shift End → Actual OUT (not IN→OUT duration).
              </p>
            ) : (
              <p className="text-xs text-slate-500 mt-3">
                Manual OT remains unchanged when attendance punches change.
              </p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className="px-4 py-2 text-sm rounded-lg border border-slate-200" onClick={() => setDetail(null)}>
                Close
              </button>
              {canEdit && detail.status === "PENDING" ? (
                <button type="button" className="ot-save-btn" disabled={saving} onClick={() => onApprove(detail)}>
                  Approve for payroll
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {addOpen ? (
        <div className="ot-modal-backdrop" role="presentation" onClick={() => !saving && setAddOpen(false)}>
          <div className="ot-modal ot-modal--pay" role="dialog" aria-labelledby="ot-add-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="ot-add-title" className="text-lg font-bold text-slate-900">
              {occurrenceModeHint || occurrenceMode ? "Add OT Occurrence" : "Add OT"}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {occurrenceModeHint || occurrenceMode
                ? "Each save ticks the next occurrence checkbox for this employee. Payroll pays approved occurrences × the fixed amount."
                : "Manual entry — OT Hours is the authoritative value. Attendance punches will not overwrite it."}
            </p>
            <div className="mt-4 space-y-3">
              <EmployeeSelect
                value={addForm.employee_id}
                onChange={(id) => setAddForm((f) => ({ ...f, employee_id: id || "" }))}
                label="Employee *"
                required
              />
              <label className="block text-sm font-semibold text-slate-700">
                Date *
                <input
                  type="date"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={addForm.work_date}
                  onChange={(e) => setAddForm((f) => ({ ...f, work_date: e.target.value }))}
                />
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                {occurrenceModeHint || occurrenceMode
                  ? "OT Hours (stored for attendance; pay uses occurrence count) *"
                  : "OT Hours *"}
                <input
                  type="number"
                  min={0.25}
                  step={0.25}
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={addForm.hours}
                  onChange={(e) => setAddForm((f) => ({ ...f, hours: e.target.value }))}
                />
              </label>
              <OtPayMath
                employeeId={addForm.employee_id}
                workDate={addForm.work_date}
                hours={addForm.hours}
                canEdit={canEdit}
                autoScroll
                revision={payRevision}
                onSaved={() => setPayRevision((n) => n + 1)}
              />
              <label className="block text-sm font-semibold text-slate-700">
                Remarks
                <textarea
                  rows={2}
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={addForm.remarks}
                  onChange={(e) => setAddForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </label>
            </div>
            {addError ? <p className="text-sm text-red-600 mt-2">{addError}</p> : null}
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-slate-200"
                disabled={saving}
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="ot-save-btn" disabled={saving} onClick={submitAdd}>
                {saving ? "Saving…" : occurrenceModeHint || occurrenceMode ? "Save & tick" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
